// ─────────────────────────────────────────────────────────────────────────────
// Fila de retorno — os tickets com hora marcada para voltar ao cliente.
//
// No MySuite eles ficam no status 5. Cada um carrega `dataPrevisao` e
// `horaPrevisao`: é o compromisso que o operador assumiu com o solicitante.
//
// Este módulo é só regra — não conhece React nem rede, e por isso roda inteiro
// sob `node` em scripts/teste-fila-retorno.mjs.
//
// A decisão que orienta o resto: o horário agendado é um compromisso, e chegar
// atrasado nele custa mais do que qualquer outra fila. Por isso o aviso sai
// ANTES da hora, e um prazo vencido continua aparecendo em vez de sumir da
// lista — sumir daria a impressão de que foi resolvido.
// ─────────────────────────────────────────────────────────────────────────────

/** Quanto tempo antes do horário o operador é avisado. */
export const ANTECEDENCIA_MIN = 5;

/**
 * O alerta de um agendamento sai UMA VEZ, e só.
 *
 * Antes ele voltava a cada 3 minutos enquanto o ticket estivesse iminente ou
 * vencido. A intenção era não deixar um compromisso passar em branco; o efeito
 * foi outro — um modal que reabre a cada 3 minutos vira ruído, e o operador
 * aprende a fechá-lo no reflexo, inclusive quando ele importava.
 *
 * O compromisso não fica desamparado por isso: depois do alerta único o ticket
 * continua visível no cartão da tela de início, em "Agendamento excedido", até
 * ser tratado. Ver `agendamentosExcedidos`, aqui embaixo.
 */
export const ALERTA_UNICO = true;

/** Status do MySuite para "fila de retorno". */
export const STATUS_FILA_RETORNO = 5;

/**
 * O prazo de um ticket, como Date. `null` quando não dá para saber.
 *
 * A API não é consistente no formato: a data vem "2026-08-22" ou "22/08/2026",
 * às vezes com a hora grudada num ISO. A hora vem "14:30" ou "14:30:00". Aceitar
 * as variações aqui evita espalhar `if` de formato pelas telas.
 *
 * Um ticket agendado sem data é tratado como sem prazo — e não como prazo zero,
 * que o colocaria no topo da lista como se fosse o mais urgente de todos.
 */
export function prazoDoTicket(ticket) {
  if (!ticket) return null;

  const bruta = String(ticket.dataPrevisao || ticket.data_previsao || '').trim();
  if (!bruta || bruta.startsWith('0000')) return null;

  let ano;
  let mes;
  let dia;

  const iso = bruta.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const br = bruta.match(/^(\d{2})\/(\d{2})\/(\d{4})/);

  if (iso) { [, ano, mes, dia] = iso; }
  else if (br) { [, dia, mes, ano] = br; }
  else return null;

  const hora = String(ticket.horaPrevisao || ticket.hora_previsao || '').trim();
  const hm = hora.match(/^(\d{1,2}):(\d{2})/);
  const h = hm ? Number(hm[1]) : 0;
  const m = hm ? Number(hm[2]) : 0;

  const d = new Date(Number(ano), Number(mes) - 1, Number(dia), h, m, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Minutos que faltam para o prazo. Negativo quando já passou.
 * `null` quando o ticket não tem prazo.
 */
export function minutosAte(ticket, agora = new Date()) {
  const prazo = prazoDoTicket(ticket);
  if (!prazo) return null;
  return Math.round((prazo.getTime() - agora.getTime()) / 60000);
}

/**
 * A situação do prazo, que a tela usa para escolher a cor.
 *
 *   vencido  · passou da hora
 *   iminente · dentro da antecedência do aviso
 *   proximo  · ainda hoje, em menos de uma hora
 *   distante · o resto
 *   sem-prazo· agendado sem data (existe, e é melhor mostrar que esconder)
 */
export function situacaoDoPrazo(ticket, agora = new Date()) {
  const min = minutosAte(ticket, agora);
  if (min === null) return 'sem-prazo';
  if (min < 0) return 'vencido';
  if (min <= ANTECEDENCIA_MIN) return 'iminente';
  if (min <= 60) return 'proximo';
  return 'distante';
}

/**
 * Texto curto do prazo, para o card.
 * Fala em minutos e horas porque é assim que o operador pensa a agenda dele —
 * "14:30" obriga a fazer a conta de cabeça para saber se dá tempo.
 */
export function textoDoPrazo(ticket, agora = new Date()) {
  const min = minutosAte(ticket, agora);
  if (min === null) return 'sem horário definido';

  if (min < 0) {
    const atraso = Math.abs(min);
    if (atraso < 60) return `venceu há ${atraso} min`;
    const h = Math.floor(atraso / 60);
    return atraso < 1440 ? `venceu há ${h}h` : `venceu há ${Math.floor(atraso / 1440)} dia(s)`;
  }

  if (min === 0) return 'agora';
  if (min < 60) return `em ${min} min`;
  if (min < 1440) {
    const h = Math.floor(min / 60);
    const resto = min % 60;
    return resto ? `em ${h}h${String(resto).padStart(2, '0')}` : `em ${h}h`;
  }
  return `em ${Math.floor(min / 1440)} dia(s)`;
}

/** Hora marcada, como "22/08 14:30". Vazio quando não há prazo. */
export function horarioFormatado(ticket) {
  const prazo = prazoDoTicket(ticket);
  if (!prazo) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(prazo.getDate())}/${pad(prazo.getMonth() + 1)} `
    + `${pad(prazo.getHours())}:${pad(prazo.getMinutes())}`;
}

/**
 * Ordena pelo que precisa de atenção primeiro: o que venceu, depois o que está
 * mais perto, e por último o que não tem prazo.
 */
export function ordenarPorPrazo(tickets, agora = new Date()) {
  return [...(tickets || [])].sort((a, b) => {
    const ma = minutosAte(a, agora);
    const mb = minutosAte(b, agora);
    if (ma === null && mb === null) return 0;
    if (ma === null) return 1;     // sem prazo vai para o fim
    if (mb === null) return -1;
    return ma - mb;
  });
}

/** Quantos já venceram — o número que o card destaca. */
export function quantosVencidos(tickets, agora = new Date()) {
  return (tickets || []).filter(t => situacaoDoPrazo(t, agora) === 'vencido').length;
}

/** O mais urgente da lista, ou null. */
export function maisUrgente(tickets, agora = new Date()) {
  const ordenados = ordenarPorPrazo(tickets, agora);
  return ordenados.find(t => minutosAte(t, agora) !== null) || null;
}

/**
 * A identidade de um aviso — o que decide se este ticket já foi avisado.
 *
 * Não é só o número: é o número MAIS o compromisso, ou seja, a data e a hora de
 * previsão. Reagendar o ticket para outro horário produz uma identidade nova, e
 * ele recupera o direito ao seu alerta único — o certo, porque o compromisso
 * passou a ser outro.
 *
 * O status não entra na chave porque não precisa entrar: esta lista é
 * consultada com `codigoStatus: 5` (ver `ticketsFilaRetorno`, em mysuiteApi.js).
 * Estar na lista JÁ É estar na fila de retorno; saiu do status, sumiu da lista,
 * e nem chega aqui para ser comparado.
 */
export function identidadeDoAviso(ticket) {
  const numero = numeroDoTicket(ticket);
  if (numero == null) return null;

  const data = String(ticket.dataPrevisao || ticket.data_previsao || '').trim();
  const hora = String(ticket.horaPrevisao || ticket.hora_previsao || '').trim();
  return `${numero}|${data}|${hora}`;
}

/**
 * Quais tickets merecem o alerta agora.
 *
 * `avisados` é um mapa { [identidade]: instanteDoAviso }, mantido em disco pelo
 * chamador (ver services/avisosRetorno.js). A regra é de uma linha: entra quem
 * está na hora — iminente ou já vencido — e ainda não foi avisado NESTE
 * agendamento.
 *
 * Um ticket VENCIDO também entra na primeira vez: o operador pode ter aberto o
 * Orion depois da hora marcada, e nesse caso avisar tarde é melhor do que não
 * avisar. O que não acontece mais é o alerta voltar sozinho depois disso.
 */
export function aAvisar(tickets, agora = new Date(), avisados = {}) {
  return (tickets || []).filter((t) => {
    const situacao = situacaoDoPrazo(t, agora);
    if (situacao !== 'iminente' && situacao !== 'vencido') return false;

    const identidade = identidadeDoAviso(t);
    if (!identidade) return false;

    return avisados[identidade] == null;
  });
}

/**
 * Os agendamentos excedidos: passou da hora e o alerta já foi dado.
 *
 * É o que sobra do alerta único. Ele aparece uma vez e sai da frente; a partir
 * daí o ticket vive aqui, no cartão da tela de início, onde não interrompe
 * ninguém mas também não deixa esquecer.
 *
 * As duas condições do pedido estão embutidas:
 *   · MESMA data de previsão — a identidade guardada carrega data e hora, então
 *     um ticket reagendado não casa com o registro, sai desta lista e volta a
 *     ser um agendamento comum, com direito a novo alerta;
 *   · MESMO status de fila de retorno — quem sai do status 5 desaparece da
 *     lista consultada, e portanto daqui junto.
 */
export function agendamentosExcedidos(tickets, agora = new Date(), avisados = {}) {
  return (tickets || []).filter((t) => {
    if (situacaoDoPrazo(t, agora) !== 'vencido') return false;
    const identidade = identidadeDoAviso(t);
    return identidade != null && avisados[identidade] != null;
  });
}

/**
 * O número do ticket, qualquer que seja o nome do campo na resposta.
 * A API do MySuite usa `codigo` na fila de pendentes e `numero` em outros
 * pontos; aceitar os dois evita um card que abre sem número.
 */
export function numeroDoTicket(ticket) {
  if (!ticket) return null;
  const bruto = ticket.codigo ?? ticket.numero ?? ticket.numeroTicket ?? ticket.numero_ticket;
  const n = parseInt(bruto, 10);
  return Number.isFinite(n) ? n : null;
}

/** Assunto do ticket, com os nomes de campo que a API usa. */
export function assuntoDoTicket(ticket) {
  return String(
    ticket?.assunto || ticket?.titulo || ticket?.descricao || ''
  ).trim() || 'Sem assunto';
}

/** Nome do cliente, quando a resposta traz. */
export function clienteDoTicket(ticket) {
  return String(
    ticket?.nomeCliente || ticket?.cliente || ticket?.razaoSocial || ''
  ).trim();
}
